import { TruckIcon, CheckCircleIcon, MapPinIcon, ClockIcon } from '@phosphor-icons/react';

import { useOrderTrackingEvents } from '../../../order/models';
import { useTranslation } from '../../../../contexts/translation-context';
import { formatDateTime } from '../../../../utils/date';
import { SpinnerBlock } from '../../../../components/Spinner';
import type { OrderTrackingEvent } from '../../../order/endpoints/OrderTrackingEventsEndpoint.types';
import { ErrorState } from '../../../../components/error-state/ErrorState';

interface OrderTrackingEventsProps {
  orderId: string;
}

const EVENT_ICONS: Record<string, React.ReactNode> = {
  StatusLoaded: <TruckIcon className="w-5 h-5 text-positive-text" weight="fill" />,
  StatusUnloaded: <CheckCircleIcon className="w-5 h-5 text-info" weight="fill" />,
  LocationPing: <MapPinIcon className="w-5 h-5 text-fg-muted" />,
};

export const OrderTrackingEvents: React.FC<OrderTrackingEventsProps> = ({ orderId }) => {
  const { i18n } = useTranslation();
  const { data: events, error, isLoading, mutate } = useOrderTrackingEvents(orderId);

  if (isLoading) {
    return <SpinnerBlock message={i18n('carrierPortal.tracking.loading')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => mutate()} variant="section" />;
  }

  if (!events || events.length === 0) {
    return <div className="text-fg-muted text-sm py-4">{i18n('carrierPortal.tracking.noEvents')}</div>;
  }

  // Filter out frequent LocationPing events, keep only important ones
  // Show last LocationPing and all status change events
  const filteredEvents = events.filter((event, index) => {
    if (event.eventType !== 'LocationPing') return true;
    // Keep first LocationPing or if it's been more than 30 min since last shown
    const nextEvent = events[index + 1];
    if (!nextEvent || nextEvent.eventType !== 'LocationPing') return true;
    const timeDiff = new Date(event.createdAt).getTime() - new Date(nextEvent.createdAt).getTime();
    return timeDiff > 30 * 60 * 1000;
  });

  return (
    <div className="space-y-3">
      {filteredEvents.map((event) => (
        <EventItem key={event.id} event={event} i18n={i18n} />
      ))}
    </div>
  );
};

interface EventItemProps {
  event: OrderTrackingEvent;
  i18n: ReturnType<typeof useTranslation>['i18n'];
}

const EventItem: React.FC<EventItemProps> = ({ event, i18n }) => {
  return (
    <div className="flex items-start gap-3 p-3 bg-surface-sunken rounded-lg">
      <div className="shrink-0 mt-1">{EVENT_ICONS[event.eventType] || <ClockIcon className="w-5 h-5" />}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{i18n(`carrierPortal.tracking.eventTypes.${event.eventType}`)}</div>
        <div className="text-fg-muted text-sm">{formatDateTime(event.createdAt)}</div>
        {event.stopName && (
          <div className="text-fg-muted text-sm">
            {i18n('carrierPortal.tracking.stop')}: {event.stopName}
          </div>
        )}
        {event.actorName && (
          <div className="text-fg-muted text-sm">
            {i18n('carrierPortal.tracking.by')} {event.actorName}
          </div>
        )}
      </div>
    </div>
  );
};
